import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useEffect } from 'react';
import {
  AssessmentCard,
  ControlledDateField,
  ControlledSelectField,
  ControlledTextField,
} from '../../shared';
import { rules } from '../../demographics/rules';
import { stateOptions } from '../../../../helpers/states';
import {
  selectMedicalInfoAnswers,
  selectPatient,
} from '../../../../redux/assessment';
import { PrimaryPolicyHolderFormValues } from '.';
import { useExperimentation } from '../../../../experimentation/client';

type PropTypes = {
  form: UseFormReturn<any>; // TODO TAP-2613: need to update this to be the correct type
};

export const PrimaryPolicyHolderForm = ({ form }: PropTypes) => {
  const { control, setValue, watch } = form;
  const convertDateToUTC = (date: Date): Date =>
    new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const patient = useSelector(selectPatient);
  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);
  const [shouldShowInsuranceComboScreen, comboScreenFlagIsValid] =
    useExperimentation({ flagName: 'show_insurance_combo_screen' });

  const prevPrimaryPolicyholder =
    medicalInfoAnswers.MI_PrimaryPolicyHolderDetails;

  const isRelationToPatientOther = watch('relationToPatient') === 'Other';
  const isAddressSameAsPatient = watch('isPolicyholderAddressSameAsPatient');

  const populateOrClearAllAddressFields = () => {
    const fieldToValueMap: Partial<
      Record<keyof PrimaryPolicyHolderFormValues, string>
    > = {
      addressStreet1: isAddressSameAsPatient
        ? patient.addressStreet1
        : prevPrimaryPolicyholder?.addressStreet1 ?? '',
      addressStreet2: isAddressSameAsPatient
        ? patient.addressStreet2
        : prevPrimaryPolicyholder?.addressStreet2 ?? '',
      addressCity: isAddressSameAsPatient
        ? patient.addressCity
        : prevPrimaryPolicyholder?.addressCity ?? '',
      addressState: isAddressSameAsPatient
        ? patient.addressState
        : prevPrimaryPolicyholder?.addressState ?? '',
      addressZipCode: isAddressSameAsPatient
        ? patient.addressZipCode
        : prevPrimaryPolicyholder?.addressZipCode ?? '',
    };

    const updateAddressValue = (
      name: keyof PrimaryPolicyHolderFormValues,
      value: string
    ) => {
      setValue(name, value, {
        shouldValidate: !!isAddressSameAsPatient,
      });
    };

    Object.entries(fieldToValueMap).forEach(
      ([key, value]: [keyof PrimaryPolicyHolderFormValues, string]) => {
        updateAddressValue(key, value);
      }
    );
  };

  useEffect(() => {
    populateOrClearAllAddressFields();
  }, [isAddressSameAsPatient]);

  return (
    <>
      <AssessmentCard>
        <Grid container columnSpacing={1} rowSpacing={2}>
          <Grid item xs={12}>
            <Typography component="h4" variant="h6">
              Policyholder's information
            </Typography>
            {comboScreenFlagIsValid && !shouldShowInsuranceComboScreen && (
              <Typography variant="body2">
                To avoid issues processing your insurance, be sure to provide
                the policyholder’s information below.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<PrimaryPolicyHolderFormValues>
              name="firstName"
              control={control}
              defaultValue={prevPrimaryPolicyholder?.firstName ?? ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="First name"
              rules={rules.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<PrimaryPolicyHolderFormValues>
              name="lastName"
              control={control}
              defaultValue={prevPrimaryPolicyholder?.lastName ?? ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Last name"
              rules={rules.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledDateField<PrimaryPolicyHolderFormValues>
              name="birthDate"
              form={form}
              defaultValue={
                prevPrimaryPolicyholder?.birthDate
                  ? convertDateToUTC(
                      new Date(prevPrimaryPolicyholder.birthDate)
                    )
                  : null
              }
              label="Date of birth"
              rules={rules.birthDate}
            />
          </Grid>
          {isRelationToPatientOther && (
            <Grid item xs={12} sm={6}>
              <ControlledTextField<PrimaryPolicyHolderFormValues>
                name="customRelation"
                control={control}
                defaultValue={prevPrimaryPolicyholder?.customRelation ?? ''}
                label="Relation to patient"
                rules={rules.customRelation}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          )}
        </Grid>
      </AssessmentCard>
      <AssessmentCard>
        <Grid container columnSpacing={1} rowSpacing={2}>
          <Grid item xs={12}>
            <Typography component="h4" variant="h6">
              Address
            </Typography>
            <Controller
              name="isPolicyholderAddressSameAsPatient"
              defaultValue={prevPrimaryPolicyholder?.isAddressLinkedWithPatient}
              control={control}
              render={({ field: { value, ...field } }) => (
                <FormControlLabel
                  {...field}
                  control={<Checkbox checked={value} />}
                  label="Address same as patient"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<PrimaryPolicyHolderFormValues>
              name="addressStreet1"
              control={control}
              defaultValue={
                isAddressSameAsPatient ? patient.addressStreet1 : ''
              }
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Street"
              disabled={isAddressSameAsPatient}
              rules={rules.addressStreet1}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<PrimaryPolicyHolderFormValues>
              name="addressStreet2"
              control={control}
              defaultValue={
                isAddressSameAsPatient ? patient.addressStreet2 : ''
              }
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Apt, suite, etc. (optional)"
              disabled={isAddressSameAsPatient}
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField<PrimaryPolicyHolderFormValues>
              name="addressCity"
              control={control}
              defaultValue={isAddressSameAsPatient ? patient.addressCity : ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="City"
              disabled={isAddressSameAsPatient}
              rules={rules.addressCity}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectField<PrimaryPolicyHolderFormValues>
              name="addressState"
              control={control}
              defaultValue={isAddressSameAsPatient ? patient.addressState : ''}
              options={stateOptions}
              label="State"
              disabled={isAddressSameAsPatient}
              rules={rules.addressState}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<PrimaryPolicyHolderFormValues>
              name="addressZipCode"
              control={control}
              defaultValue={
                isAddressSameAsPatient ? patient.addressZipCode : ''
              }
              autoComplete="on"
              inputMode="numeric"
              label="ZIP"
              disabled={isAddressSameAsPatient}
              rules={rules.addressZipCode}
            />
          </Grid>
        </Grid>
      </AssessmentCard>
    </>
  );
};
